/**
 * ThemeContext - Gestion centralisée du thème clair/sombre
 * Permet de basculer entre les modes avec des couleurs cohérentes
 */

import React, {createContext, useContext, useState, useEffect, ReactNode} from 'react';
import {useColorScheme, Platform} from 'react-native';
import storageService from '../services/storageService';
import {LightTheme, DarkTheme, ReadingLightTheme, ReadingDarkTheme, Theme} from '../theme/theme';

interface ThemeContextType {
  theme: Theme;
  isDarkMode: boolean;
  isReadingMode: boolean;
  toggleTheme: () => void;
  toggleReadingMode: () => void;
  setThemeMode: (mode: 'light' | 'dark' | 'auto') => void;
  setReadingMode: (enabled: boolean) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({children}) => {
  const systemColorScheme = useColorScheme();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isReadingMode, setIsReadingModeState] = useState(false);
  const [themeMode, setThemeModeState] = useState<'light' | 'dark' | 'auto'>('auto');

  // Charger les préférences au démarrage
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        await storageService.init();
        const savedThemeMode = await storageService.getItem('theme_mode');
        const savedReadingMode = await storageService.getItem('reading_mode');
        
        // Charger le mode lecture
        if (savedReadingMode === 'true') {
          setIsReadingModeState(true);
        }
        
        if (savedThemeMode === 'dark') {
          setThemeModeState('dark');
          setIsDarkMode(true);
        } else if (savedThemeMode === 'light') {
          setThemeModeState('light');
          setIsDarkMode(false);
        } else {
          // Mode auto : utiliser la préférence système
          setThemeModeState('auto');
          if (Platform.OS === 'web' && typeof window !== 'undefined' && window.matchMedia) {
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            setIsDarkMode(prefersDark);
          } else {
            setIsDarkMode(systemColorScheme === 'dark');
          }
        }
      } catch (error) {
        console.error('[ThemeContext] Failed to load theme preferences:', error);
        // Fallback : utiliser le thème système
        setIsDarkMode(systemColorScheme === 'dark');
      }
    };

    loadPreferences();
  }, [systemColorScheme]);

  // Toggle entre clair et sombre
  const toggleTheme = async () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    setThemeModeState(newMode ? 'dark' : 'light');
    
    try {
      await storageService.setItem('theme_mode', newMode ? 'dark' : 'light');
    } catch (error) {
      console.error('[ThemeContext] Failed to save theme preference:', error);
    }
  };

  // Toggle mode lecture
  const toggleReadingMode = async () => {
    const newMode = !isReadingMode;
    setIsReadingModeState(newMode);
    
    try {
      await storageService.setItem('reading_mode', newMode ? 'true' : 'false');
    } catch (error) {
      console.error('[ThemeContext] Failed to save reading mode preference:', error);
    }
  };

  // Définir explicitement le mode lecture
  const setReadingMode = async (enabled: boolean) => {
    setIsReadingModeState(enabled);
    
    try {
      await storageService.setItem('reading_mode', enabled ? 'true' : 'false');
    } catch (error) {
      console.error('[ThemeContext] Failed to save reading mode:', error);
    }
  };

  // Définir le mode de thème (light, dark, auto)
  const setThemeMode = async (mode: 'light' | 'dark' | 'auto') => {
    setThemeModeState(mode);
    
    if (mode === 'auto') {
      // Utiliser la préférence système
      if (Platform.OS === 'web' && typeof window !== 'undefined' && window.matchMedia) {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        setIsDarkMode(prefersDark);
      } else {
        setIsDarkMode(systemColorScheme === 'dark');
      }
      await storageService.setItem('theme_mode', 'auto');
    } else {
      setIsDarkMode(mode === 'dark');
      await storageService.setItem('theme_mode', mode);
    }
  };

  // Sélectionner le thème en fonction du mode sombre ET du mode lecture
  const getTheme = (): Theme => {
    if (isReadingMode) {
      return isDarkMode ? ReadingDarkTheme : ReadingLightTheme;
    }
    return isDarkMode ? DarkTheme : LightTheme;
  };

  const theme = getTheme();

  return (
    <ThemeContext.Provider value={{
      theme, 
      isDarkMode, 
      isReadingMode,
      toggleTheme, 
      toggleReadingMode,
      setThemeMode,
      setReadingMode,
    }}>
      {children}
    </ThemeContext.Provider>
  );
};
